'use client';

import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  getSynchronyUrls,
  type SynchronyUrls,
} from '~/hooks/use-synchrony-urls';
import { isServer } from '~/utils/host';

type Props = {
  triggerFlow: 'applyNow' | 'prequalify';
  orderId: string;
  setLoadingAction: Dispatch<SetStateAction<boolean>>;
};

export default function ApplyInSite({
  triggerFlow,
  orderId,
  setLoadingAction,
}: Props) {
  const [urls, setUrls] = useState<SynchronyUrls | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    setLoadingAction(true);
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    getSynchronyUrls(orderId, triggerFlow)
      .then(setUrls)
      .catch((err) => {
        setUrls(urls);
        setLoadingAction(false);
        console.error('Failed to fetch Synchrony URLs:', err);
      });
  }, [orderId, triggerFlow, setLoadingAction]);

  useEffect(() => {
    if (!urls) return;
    if (isServer) return;
    setLoadingAction(true);
    const { js } = getSyfApplyInSiteUrls();

    document
      .querySelectorAll(`script[src="${js}"]`)
      .forEach((el) => el.remove());

    setScriptLoaded(false);

    const script = document.createElement('script');
    script.src = js;
    script.async = true;
    script.onload = () => {
      console.info('SYF client script loaded');
      setScriptLoaded(true);
      setLoadingAction(false);
      console.log('syfClient script loaded');
    };

    script.onerror = () => {
      setLoadingAction(false);
      console.error('Failed to load syfClient script');
    };
    script.onerror = () => {
      console.error('Failed to load SYF client script');
    };

    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, [setLoadingAction, urls]);

  useEffect(() => {
    if (!scriptLoaded || !urls) return;
    if (isServer) return;

    setLoadingAction(true);

    const syfClient = (window as Window).syfClient;
    if (typeof syfClient !== 'function') {
      setLoadingAction(false);
      console.error('syfClient is not available on window');
      return;
    }

    const btnId = `btn-${triggerFlow}`;
    const btn = document.createElement('button');
    btn.id = btnId;
    btn.style.opacity = '0';
    btn.style.pointerEvents = 'none';
    btn.style.width = '100%';
    btn.style.height = '100%';
    btn.textContent = `Hidden ${triggerFlow}`;
    containerRef.current?.appendChild(btn);

    syfClient({
      dApplyOpener: [btnId],
      dApplyUrl: triggerFlow === 'applyNow' ? urls.apply : urls.prequalify,
      closureCallback: () => {
        setLoadingAction(false);
        console.info(`${triggerFlow} flow closed`);
        document.getElementById(btnId)?.remove();
      },
    });

    const clickTimer = setTimeout(() => {
      btn.click();
      setLoadingAction(false);
      console.info(`Clicked hidden button #${btnId}`);
    }, 50);

    return () => {
      clearTimeout(clickTimer);
      document.getElementById(btnId)?.remove();
    };
  }, [scriptLoaded, triggerFlow, urls, setLoadingAction]);

  return (
    <>
      {typeof window !== 'undefined' && (
        <link rel="stylesheet" href={getSyfApplyInSiteUrls().css} />
      )}
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      />
    </>
  );
}

interface SyfClientOptions {
  dApplyOpener: string[];
  dApplyUrl: string;
  closureCallback?: () => void;
}

declare global {
  interface Window {
    syfClient: (options: SyfClientOptions) => void;
  }
}

const getSyfApplyInSiteUrls = () => {
  if (isServer) {
    return { css: '', js: '' };
  }
  const hostname = window.location.hostname.toLowerCase();
  const isProd =
    hostname === 'mattressfirm.com' || hostname === 'www.mattressfirm.com';

  return {
    css: isProd
      ? 'https://etail.mysynchrony.com/eapply/resource/css/syfClient.css'
      : 'https://uetail.mysynchrony.com/eapply/resource/css/syfClient.css',
    js: isProd
      ? 'https://etail.mysynchrony.com/eapply/resource/js/syfClient.js'
      : 'https://uetail.mysynchrony.com/eapply/resource/js/syfClient.js',
  };
};
